// The roles an imported file can take in a release (ART-369) — the client half of the server's ImportRoles. The
// server is the authority (it refuses anything else at create, and the worker checks the bytes); this table only
// decides what each row's dropdown offers, so a person is never handed a choice the server will refuse.

import { MASTER_ROLE, suggestRoleFromFilename } from "./releaseSongFiles.js";
import { labelForVariantKey } from "./releaseVariantKinds.js";

export type ImportRole = "cover" | "canvas" | "photo" | "master" | "version" | "lyrics" | "file";

export const IMPORT_ROLE_LABELS: Record<ImportRole, string> = {
  cover: "Cover artwork",
  canvas: "Spotify Canvas",
  photo: "Photo",
  master: "Song master",
  version: "Song version",
  lyrics: "Lyrics",
  file: "File",
};

/** Dropdown order: the single slots first, then song files, then the open-ended ones. */
const ORDER: ImportRole[] = ["cover", "canvas", "lyrics", "master", "version", "photo", "file"];

/**
 * What a file is, from its NAME — the client copy of the server's StorageFileKinds, because the Chooser reports no
 * type. The server re-derives it at create and checks image bytes in the worker, so a disagreement can only make a
 * row refused, never a wrong file placed. Only the image types the server can verify count as images.
 */
const KIND_BY_EXTENSION: Record<string, string> = {
  jpg: "image", jpeg: "image", png: "image", webp: "image", gif: "image",
  wav: "audio", aif: "audio", aiff: "audio", mp3: "audio", flac: "audio", m4a: "audio", aac: "audio", ogg: "audio", opus: "audio",
  mp4: "video", m4v: "video", mov: "video", webm: "video",
  pdf: "pdf",
  txt: "document", md: "document", rtf: "document", csv: "document", doc: "document", docx: "document",
  xls: "document", xlsx: "document", ppt: "document", pptx: "document", pages: "document",
};

export function mediaKindOf(name: string): string {
  const ext = /\.([^.]+)$/.exec(name)?.[1]?.toLowerCase() ?? "";
  return KIND_BY_EXTENSION[ext] ?? "other";
}

/** Which roles accept a file of this media kind (`mediaKindOf`) and name. */
export function rolesFor(mediaKind: string | null | undefined, name: string): ImportRole[] {
  return ORDER.filter((role) => accepts(role, mediaKind ?? "other", name));
}

function accepts(role: ImportRole, kind: string, name: string): boolean {
  switch (role) {
    case "cover":
    case "photo":
      return kind === "image";
    case "canvas":
      return kind === "image" || kind === "video";
    case "master":
    case "version":
      return kind === "audio";
    case "lyrics":
      return kind === "document" && isPlainText(name);
    case "file":
      return true;
  }
}

/** Only plain text can be read into the Lyrics tile; a .docx is a zip and goes to Files. */
export function isPlainText(name: string): boolean {
  return /\.(txt|md)$/i.test(name);
}

/** Roles that fill one slot, so only one row in an import may take each. A master is one per song. */
export function isSingleRole(role: ImportRole): boolean {
  return role === "cover" || role === "canvas" || role === "lyrics";
}

export function needsSong(role: ImportRole): boolean {
  return role === "master" || role === "version";
}

export interface ImportCandidate {
  id: string;
  name: string;
  mediaKind?: string | null;
}

export interface SongRef {
  trackId: string;
  title: string;
}

export interface ImportRowDraft {
  role: ImportRole;
  trackId?: string;
  /** A file's label, or a version's kind label ("Instrumental"). */
  label?: string;
  photoRole?: string;
}

const normalise = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/** The song a file name mentions, longest title first so "Arena Heart (Live)" beats "Arena Heart". */
export function matchSong(fileName: string, songs: SongRef[]): SongRef | undefined {
  const name = ` ${normalise(fileName)} `;
  return [...songs]
    .sort((a, b) => b.title.length - a.title.length)
    .find((s) => normalise(s.title).length > 0 && name.includes(` ${normalise(s.title)} `));
}

/**
 * A best guess for every picked file, overridable row by row. The guesses never collide: a second "cover" becomes
 * a photo, a second master for one song becomes a file, and so on — the server would refuse the collision anyway,
 * and a guess that cannot be submitted is not much of a guess.
 */
export function suggestRoles(files: ImportCandidate[], songs: SongRef[]): Map<string, ImportRowDraft> {
  const out = new Map<string, ImportRowDraft>();
  const taken = new Set<string>();
  const claim = (key: string) => (taken.has(key) ? false : (taken.add(key), true));

  for (const f of files) {
    const kind = f.mediaKind ?? "other";
    const lower = f.name.toLowerCase();
    let draft: ImportRowDraft = { role: "file", label: f.name };

    if (kind === "image") {
      if (/canvas/.test(lower) && claim("canvas")) draft = { role: "canvas" };
      else if (/(cover|artwork|front)/.test(lower) && claim("cover")) draft = { role: "cover" };
      else draft = { role: "photo", photoRole: "Press" };
    } else if (kind === "video" && /canvas/.test(lower) && claim("canvas")) {
      draft = { role: "canvas" };
    } else if (kind === "document" && isPlainText(f.name) && /lyric/.test(lower) && claim("lyrics")) {
      draft = { role: "lyrics" };
    } else if (kind === "audio") {
      const song = matchSong(f.name, songs);
      const variant = suggestRoleFromFilename(f.name);
      if (song && variant === MASTER_ROLE && claim(`master:${song.trackId}`)) {
        draft = { role: "master", trackId: song.trackId };
      } else if (song && variant !== MASTER_ROLE) {
        draft = { role: "version", trackId: song.trackId, label: labelForVariantKey(variant) || undefined };
      }
    }
    out.set(f.id, draft);
  }
  return out;
}

/** Human file size: "48 MB", "1.2 GB". */
export function describeSize(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1).replace(/\.0$/, "")} GB`;
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
